import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePipedriveStore, usePopupStore, useSaleCompStore } from "../..";
import useBodyContainerClassName from "../../hooks/useBodyContainerClassName";
import useClosable from "../../hooks/useClosable";
import useNavigateToCompany from "../../hooks/useNavigateToCompany";
import useRecentlyViewed from "../../hooks/useRecentlyViewed";
import DetailsType from "../../interfaces/DetailsType";
import { RecentItemType } from "../../interfaces/IRecentItem";
import { ClosableOrder } from "../../store/ClosableStore";
import { saleCompDealProfileToString, saleCompPropertyTypeToString, saleCompSourceToString } from "../../utils/enumUtils";
import { formatDate, formatDateTime, formatNumber, formatPrice } from "../../utils/utils";
import { showErrorToast, showSuccessToast } from "../ToastContainer";
import AttachmentList from "../AttachmentList";
import commonStyles from "../Common.module.css";
import detailsStyles from "../Details.module.css";
import DetailsPanelWithFlyTo from "../DetailsPanelWithFlyTo";
import { SaleCompFavoriteCheckbox } from "../favorites/FavoriteCheckbox";
import FieldRow from "../FieldRow";
import EditIcon from "../icons/EditIcon";
import SaleCompIcon from "../icons/SaleCompIcon";
import OnMapSpinner from "../spinners/OnMapSpinner";

function SaleCompDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const saleCompStore = useSaleCompStore();
    const pipedriveStore = usePipedriveStore();
    const popupStore = usePopupStore();
    const saleComp = useMemo(() => computed(() => saleCompStore.saleComps.find(sc => sc.id.toString() === id)), [id]).get();
    const buyerPipedriveOrganization = useMemo(() => computed(() => saleComp && pipedriveStore.organizations.find(o => o.id === saleComp.pipedriveBuyerId)), [saleComp]).get();
    const sellerPipedriveOrganization = useMemo(() => computed(() => saleComp && pipedriveStore.organizations.find(o => o.id === saleComp.pipedriveSellerId)), [saleComp]).get();
    const brokeragePipedriveOrganization = useMemo(() => computed(() => saleComp && pipedriveStore.organizations.find(o => o.id === saleComp.pipedriveBrokerageId)), [saleComp]).get();
    const navigateToCompany = useNavigateToCompany();
    const bodyContainerClassName = useBodyContainerClassName();
    useRecentlyViewed(Number(id), RecentItemType.SaleComp);

    const close = () => navigate("/");
    useClosable(ClosableOrder.Medium, close);

    useEffect(() => {
        if (saleComp)
            popupStore.openSaleCompPopup(saleComp.id);

        return () => popupStore.close();
    }, [saleComp]);

    if (saleCompStore.isSaleCompsLoading.fetching)
        return <OnMapSpinner />;

    if (!saleComp)
        return null;

    const onUploadAttachments = async (files: File[]) => {
        try {
            for (const file of files) {
                await saleCompStore.uploadAttachment(saleComp.id, file);
            }

            showSuccessToast();
        } catch (e) {
            showErrorToast();
        }
    };

    const onDeleteAttachment = async (key: string) => {
        try {
            await saleCompStore.deleteAttachment(saleComp.id, key);
            showSuccessToast();
        } catch (e) {
            showErrorToast();
        }
    };

    return (
        <DetailsPanelWithFlyTo type={DetailsType.SaleCompDetails} coordinates={saleComp.centerPoint} onClose={close} controls={[
            <SaleCompFavoriteCheckbox key="favorite" id={Number(id)} className={commonStyles.clickableIcon} />,
            <EditIcon key="edit" onClick={() => navigate(`/saleComps/${id}/edit`)} />
        ]}>
            <div className={detailsStyles.header}>
                <div className={detailsStyles.headerGroup}>
                    <SaleCompIcon color="#25479A" />
                    <div className={detailsStyles.title}>
                        <span className={commonStyles.link} onClick={() => popupStore.openSaleCompPopup(saleComp.id)}>{saleComp.address}</span>
                    </div>
                </div>
            </div>
            <div className={commonStyles.separator}></div>
            <div className={detailsStyles.body}>
                <div className={bodyContainerClassName}>
                    <div className={detailsStyles.columns}>
                        <div className={[detailsStyles.column, commonStyles.rowSeparated].join(" ")}>
                            <div className={commonStyles.header}>Sale Details</div>
                            <FieldRow label="SALE PRICE">
                                {!!saleComp.salePrice && formatPrice(saleComp.salePrice)}
                            </FieldRow>
                            <FieldRow label="DATE SOLD">
                                {!!saleComp.saleDate && formatDate(saleComp.saleDate)}
                            </FieldRow>
                            <FieldRow label="GROSS $/ACRE">
                                {!!saleComp.grossPerAcre && formatPrice(saleComp.grossPerAcre)}
                            </FieldRow>
                            <FieldRow label="USABLE $/ACRE">
                                {!!saleComp.pricePerUsableAcre && formatPrice(saleComp.pricePerUsableAcre)}
                            </FieldRow>
                            <FieldRow label="BUILDING $/PSF">
                                {!!saleComp.buildingPerSqFt && formatPrice(saleComp.buildingPerSqFt)}
                            </FieldRow>
                            <FieldRow label="BUYER">
                                {buyerPipedriveOrganization ? (
                                    <span className={commonStyles.link} onClick={() => navigateToCompany(buyerPipedriveOrganization.id)}>
                                        {buyerPipedriveOrganization.name}
                                    </span>
                                ) : saleComp.buyerName}
                            </FieldRow>
                            <FieldRow label="SELLER">
                                {sellerPipedriveOrganization ? (
                                    <span className={commonStyles.link} onClick={() => navigateToCompany(sellerPipedriveOrganization.id)}>
                                        {sellerPipedriveOrganization.name}
                                    </span>
                                ) : saleComp.sellerName}
                            </FieldRow>
                        </div>
                        <div className={[detailsStyles.column, commonStyles.rowSeparated].join(" ")}>
                            <div className={commonStyles.header}>Other Terms</div>
                            <FieldRow label="CAP RATE">
                                {!!saleComp.capRate && `${formatNumber(saleComp.capRate)}%`}
                            </FieldRow>
                            <FieldRow label="NOI">
                                {!!saleComp.noi && formatPrice(saleComp.noi)}
                            </FieldRow>
                            <FieldRow label="MARKETED">
                                {saleComp.isMarketed ? "Yes" : "No"}
                            </FieldRow>
                            <FieldRow label="BROKERAGE">
                                {brokeragePipedriveOrganization ? (
                                    <a target="_blank" className={commonStyles.link} href={pipedriveStore.getLinkToOrganization(brokeragePipedriveOrganization.id)}>
                                        {brokeragePipedriveOrganization.name}
                                    </a>
                                ) : saleComp.brokerageName}
                            </FieldRow>
                        </div>
                    </div>
                    <div className={detailsStyles.columns}>
                        <div className={[detailsStyles.column, commonStyles.rowSeparated].join(" ")}>
                            <div className={commonStyles.header}>Property Details</div>
                            <FieldRow label="USABLE LAND SIZE">
                                {!!saleComp.landSizeUsable && `${formatNumber(saleComp.landSizeUsable)} Acres`}
                            </FieldRow>
                            <FieldRow label="PARCEL SIZE">
                                {!!saleComp.parcelSizeAcres && `${formatNumber(saleComp.parcelSizeAcres)} Acres`}
                            </FieldRow>
                            <FieldRow label="BUILDING SIZE">
                                {!!saleComp.buildingSize && `${formatNumber(saleComp.buildingSize)} Sq-Ft`}
                            </FieldRow>
                            <FieldRow label="ZONING">
                                {saleComp.zoning}
                            </FieldRow>
                            <FieldRow label="PROPERTY TYPE">
                                {!!saleComp.propertyType && saleCompPropertyTypeToString(saleComp.propertyType)}
                            </FieldRow>
                            <FieldRow label="DEAL PROFILE">
                                {!!saleComp.dealProfile && saleCompDealProfileToString(saleComp.dealProfile)}
                            </FieldRow>
                        </div>
                        <div className={[detailsStyles.column, commonStyles.rowSeparated].join(" ")}>
                            <div className={commonStyles.header}>Internal</div>
                            <FieldRow label="COMP SOURCE">
                                {!!saleComp.compSource && saleCompSourceToString(saleComp.compSource)}
                            </FieldRow>
                            <FieldRow label="NOTES">
                                {saleComp.notes}
                            </FieldRow>
                            <FieldRow label="LAST MODIFIED">
                                <span>{saleComp.modifiedByUser}</span>
                                <br />
                                <span>{formatDateTime(saleComp.modifiedDate)}</span>
                            </FieldRow>
                        </div>
                    </div>
                    <div className={detailsStyles.columns}>
                        <div className={detailsStyles.column}>
                            <div className={commonStyles.header}>Attachments</div>
                            <AttachmentList attachments={saleComp.attachments} onUpload={onUploadAttachments} onDelete={onDeleteAttachment} />
                        </div>
                    </div>
                </div>
            </div>
        </DetailsPanelWithFlyTo>
    );
}

export default observer(SaleCompDetails);
